import React from 'react';
import {
  // Métier / production
  Cow, ForkKnife, Pepper, Tree, Fire, Thermometer, Drop, Snowflake,
  Fish, Bone, BowlFood, Jar, Hamburger, Knife, Scissors,
  // Inventaire / catalogue / commandes
  Package, Tag, ShoppingCart, Truck, Storefront, Barcode,
  // Compta / argent
  CurrencyDollar, Receipt, ChartBar, ChartLine, ChartPie, Wallet, Bank,
  // Docs / traçabilité
  FileText, ClipboardText, LinkSimple, Camera, Calendar, Clock,
  // Statuts
  CheckCircle, Check, Warning, WarningCircle, Info, XCircle, Lock,
  // Actions UI
  Plus, Trash, PencilSimple, Eye, MagnifyingGlass, ArrowsClockwise,
  DownloadSimple, UploadSimple, FloppyDisk, X, ArrowUUpLeft, CaretRight,
  ArrowCounterClockwise, ListBullets, Lightning,
  // Cuisson / timer
  Play, Stop, Timer,
  // Navigation / système
  House, Gear, Envelope, Bell, User, UserCircle, SignOut, Buildings, Lightbulb,
} from '@phosphor-icons/react';

// Table de mapping nom métier → composant Phosphor (refonte icônes 2026-05-29).
// Remplace les emojis. Les commentaires indiquent l'emoji historique.
const MAP = {
  // ── Production / matières ──
  viande:        Cow,            // 🥩
  // Produits Escamel (distinction visuelle)
  saumon:        Fish,           // 🐟
  ribs:          Bone,           // 🍖 côtes levées
  cotes_levees:  Bone,           // 🍖
  porc:          BowlFood,       // 🫙 porc effiloché
  saucisse:      Hamburger,      // 🌭 (pas de saucisse dans Phosphor)
  saucisses:     Hamburger,      // 🌭
  sauce:         Jar,            // 🍯 sauce BBQ
  brisket:       Cow,            // 🥩
  burnt_ends:    Fire,           // 🔥
  decoupe:       Knife,          // 🔪 découpe
  parage:        Scissors,       // ✂ parage / trim
  repas:         ForkKnife,      // 🍽 (repas/représentation)
  epices:        Pepper,         // 🧂 🌶
  legume:        Pepper,         // 🌶
  laitier:       Drop,           // 🧀 (pas de fromage dans Phosphor → goutte de lait)
  bois:          Tree,           // 🪵
  fumage:        Fire,           // 🔥
  temperature:   Thermometer,    // 🌡
  congelation:   Snowflake,      // ❄
  // ── Inventaire / catalogue / commandes ──
  package:       Package,        // 📦
  emballage:     Package,        // 📦
  etiquette:     Tag,            // 🏷
  panier:        ShoppingCart,   // 🛒
  transport:     Truck,          // 🚗 🚚
  boutique:      Storefront,     // 🏪
  sku:           Barcode,        // |||
  // ── Compta / argent ──
  argent:        CurrencyDollar, // 💰 💵
  recu:          Receipt,        // 🧾
  facture:       Receipt,        // 📄 (facture)
  analytics:     ChartBar,       // 📊
  donut:         ChartPie,       // 🥧 donut / répartition
  courbe:        ChartLine,      // 📈
  encaisse:      Wallet,         // 👛
  banque:        Bank,           // 🏦
  // ── Docs / traçabilité ──
  document:      FileText,       // 📄
  liste:         ClipboardText,  // 📋
  lien:          LinkSimple,     // 🔗
  photo:         Camera,         // 📷
  date:          Calendar,       // 📅
  heure:         Clock,          // 🕐
  // ── Statuts ──
  succes:        CheckCircle,    // ✅
  check:         Check,          // ✓
  attention:     Warning,        // ⚠️
  alerte:        WarningCircle,  // ⚠
  info:          Info,           // 💡 ℹ
  erreur:        XCircle,        // ❌
  verrou:        Lock,           // 🔒 champ verrouillé
  idee:          Lightbulb,      // 💡
  // ── Actions UI ──
  ajouter:       Plus,           // ➕ +
  supprimer:     Trash,          // 🗑
  modifier:      PencilSimple,   // ✏
  voir:          Eye,            // 👁
  recherche:     MagnifyingGlass,// 🔍
  rafraichir:    ArrowsClockwise,// 🔄 ↻
  telecharger:   DownloadSimple, // ⬇
  televerser:    UploadSimple,   // ⬆
  sauvegarder:   FloppyDisk,     // 💾
  fermer:        X,              // ✕ ✖
  retour:        ArrowUUpLeft,   // ↩
  recalculer:    ArrowCounterClockwise, // ↺ recalculer
  liste_puces:   ListBullets,    // • liste à puces
  auto:          Lightning,      // ⚡ auto-calculer
  chevron:       CaretRight,     // › →
  // Cuisson / timer
  demarrer:      Play,           // ▶ démarrer cuisson
  arreter:       Stop,           // ⏹ arrêter cuisson
  timer:         Timer,          // ⏱ suivi cuisson
  // ── Navigation / système ──
  accueil:       House,          // 🏠 ◈
  parametres:    Gear,           // ⚙
  email:         Envelope,       // ✉
  notification:  Bell,           // 🔔
  utilisateur:   User,           // 👤
  compte:        UserCircle,     // 👤
  deconnexion:   SignOut,        // ⏻
  entreprise:    Buildings,      // 🏢
};

/**
 * <Icon name="viande" /> — icône SVG cohérente (remplace les emojis).
 * @param name   clé métier (voir MAP)
 * @param size   px (défaut 18)
 * @param weight thin|light|regular|bold|fill|duotone (défaut regular)
 * @param color  couleur (défaut: currentColor → hérite du texte)
 */
export default function Icon({ name, size = 18, weight = 'regular', color = 'currentColor', ...rest }) {
  const Cmp = MAP[name];
  if (!Cmp) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[Icon] nom inconnu : "${name}" — ajoute-le dans Icon.jsx MAP`);
    }
    return <WarningCircle size={size} weight={weight} color={color} {...rest} />;
  }
  return <Cmp size={size} weight={weight} color={color} {...rest} />;
}

export const ICON_NAMES = Object.keys(MAP);
